import React, { useState } from 'react';
import { Button, Form, Alert } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface CancelProjectConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  projectName?: string;
}

const CancelProjectConfirmationModal: React.FC<CancelProjectConfirmationModalProps> = ({
  visible,
  onClose,
  onConfirm,
  projectName
}) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const handleConfirm = () => {
    if (!cancellationReason.trim()) {
      setReasonError('Cancellation reason required');
      document.getElementById('cancellationReason')?.focus();
      return;
    }
    onConfirm(cancellationReason);
    setCancellationReason('');
    setReasonError(null);
    onClose();
  };

  const handleClose = () => {
    setCancellationReason('');
    setReasonError(null);
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={handleClose}
      title="Cancel Project"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleClose}>
            No
          </Button>
          <Button
            variant="danger"
            type="button"
            onClick={handleConfirm}
          >
            Yes
          </Button>
        </>
      }
    >
      <Alert variant="warning">
        <i className="bi bi-exclamation-triangle tw-mr-2"></i>
        Canceling a project will also cancel all tasks within it. This operation cannot be undone. Are you sure?
      </Alert>
      {projectName && (
        <p className="tw-mb-3">
          <strong>Project:</strong> {projectName}
        </p>
      )}
      <div className="tw-mb-3">
        <Form.Label htmlFor="cancellationReason">
          <strong>Cancellation Reason <span className="tw-text-danger">*</span></strong>
        </Form.Label>
        <Form.Control
          as="textarea"
          id="cancellationReason"
          rows={3}
          value={cancellationReason}
          onChange={(e) => {
            setCancellationReason(e.target.value);
            if (reasonError) setReasonError(null);
          }}
          placeholder="Please provide a reason for cancelling this project..."
          required
          isInvalid={!!reasonError}
          aria-invalid={!!reasonError}
          aria-describedby={reasonError ? 'cancellationReason-error' : undefined}
        />
        {reasonError && (
          <Form.Control.Feedback type="invalid" id="cancellationReason-error">
            {reasonError}
          </Form.Control.Feedback>
        )}
      </div>
    </ResponsiveModal>
  );
};

export default CancelProjectConfirmationModal;
